import {
  Box,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  Flex,
  Spacer,
  Badge,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import useInfiniteFetchEmployees from 'app/hooks/useInfiniteFetchEmployees';
import { useParams } from 'react-router-dom';
import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompany from 'app/hooks/useFetchCompany';
import { Status } from 'app/constants/individual';
import { ArrowRightOutline, SearchNormalOutline, shadowStyles } from '@aslanpay/portal-common-lib';
import { IndividualRow } from 'app/components/UI/Table/Table.types';

type RewardSelectEmployeesFormProps = {
  selectedEmployeeIds: string[];
  setSelectedEmployeeIds: (ids: string[]) => void;
  onStepAction: () => void;
  setEmployeeData: (data: IndividualRow[]) => void;
};

function RewardSelectEmployeesForm({
  selectedEmployeeIds,
  setSelectedEmployeeIds,
  onStepAction,
  setEmployeeData,
}: RewardSelectEmployeesFormProps) {
  const { companyId } = useParams();
  const { user } = useAuthContext();
  const { data: company } = useFetchCompany(companyId || user?.companyId);

  const [searchQuery, setSearchQuery] = useState('');
  const [allEmployees, setAllEmployees] = useState<IndividualRow[]>([]);

  const {
    data: employeesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
  } = useInfiniteFetchEmployees({
    companyId: company?.id,
    status: ['CREATED', 'INVITED', 'ACTIVE'],
    onUnpaidLeave: false,
    size: 50,
  });

  /* const employees = useMemo(
    () => employeesData?.pages.flatMap((page) => page.list) ?? [],
    [employeesData]
  ); */

  useEffect(() => {
    if (!isSuccess) return;

    // Flatten the list from all pages
    const flatList = employeesData.pages.flatMap((page) => page.list);
    setAllEmployees(flatList);

    // Auto-fetch next page if needed
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [employeesData, isSuccess, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (allEmployees.length > 0) {
      setEmployeeData(allEmployees);
    }
  }, [allEmployees, setEmployeeData]);

  const filteredEmployees = allEmployees.filter((emp) => {
    const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
    return (
      fullName.includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleToggleEmployee = (id: string) => {
    setSelectedEmployeeIds(
      selectedEmployeeIds.includes(id)
        ? selectedEmployeeIds.filter((i) => i !== id)
        : [...selectedEmployeeIds, id]
    );
  };

  if (isLoading) return <Spinner />;

  return (
    <VStack h="65vh" align="stretch" spacing={4}>
      <Flex justify="space-between" align="center" mt="30px" gap="30px">
        <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
          Select employees to reward points
        </Text>
        <Spacer />
        <InputGroup
          maxW="278px"
          aria-disabled={!searchQuery.length && !filteredEmployees.length}
          color="brand.darkGray"
          _disabled={{ color: 'system.disabled' }}
        >
          <Input
            placeholder="Search employees"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>

      <Box flex="1" overflowY="auto">
        <Table variant="simple" size="sm">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              <Th>
                <Checkbox
                  isChecked={
                    selectedEmployeeIds.length === filteredEmployees.length &&
                    filteredEmployees.length > 0
                  }
                  isIndeterminate={
                    selectedEmployeeIds.length > 0 &&
                    selectedEmployeeIds.length < filteredEmployees.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedEmployeeIds(filteredEmployees.map((emp) => emp.id));
                    } else {
                      setSelectedEmployeeIds([]);
                    }
                  }}
                />
              </Th>
              <Th>Aslan ID</Th>
              <Th>First name</Th>
              <Th>Last name</Th>
              <Th>Email</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          {!hasNextPage ? (
            <Tbody>
              {filteredEmployees.map((emp) => (
                <Tr key={emp.id}>
                  <Td>
                    <Checkbox
                      isChecked={selectedEmployeeIds.includes(emp.id)}
                      onChange={() => handleToggleEmployee(emp.id)}
                    />
                  </Td>
                  <Td>{emp.id}</Td>
                  <Td>{emp.firstName}</Td>
                  <Td>{emp.lastName}</Td>
                  <Td>{emp.email}</Td>
                  <Td verticalAlign="middle">
                    <Badge
                      colorScheme={`badge.${Status[emp.status]?.color ?? 'gray'}`}
                      size="md"
                      px={3}
                      py={1}
                      borderRadius="md"
                    >
                      {Status[emp.status]?.value ?? emp.status}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          ) : (
            <Tbody>
              <Tr>
                <Td colSpan={5}>
                  <Spinner size="md" />
                </Td>
              </Tr>
            </Tbody>
          )}
        </Table>
      </Box>
      {selectedEmployeeIds.length > 0 && (
        <Flex
          width="full"
          gap="10px"
          px="16px"
          py="10px"
          alignItems="center"
          borderRadius="10px"
          boxShadow={shadowStyles.dropdownMenu}
          bgColor="brand.mutedPurple"
          color="brand.white"
          position="sticky"
          bottom="20px"
          zIndex="998"
        >
          {selectedEmployeeIds.length === allEmployees.length && (
            <Text textStyle="h6Medium" fontWeight="bold">
              All
            </Text>
          )}
          <Text
            minW="40px"
            p="10px"
            borderRadius="5px"
            bgColor="brand.purple"
            textAlign="center"
            textStyle="h5SemiBold"
          >
            {selectedEmployeeIds.length}
          </Text>
          <Text textStyle="h6Medium">employees selected</Text>
          <Spacer />
          <Button
            variant="solid"
            colorScheme="whiteAlpha"
            onClick={onStepAction}
            rightIcon={<ArrowRightOutline boxSize="20px" />}
          >
            Next
          </Button>
        </Flex>
      )}
    </VStack>
  );
}

export default RewardSelectEmployeesForm;
